import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import { Grid, Spin, Tooltip, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";

import { loadCalls, setCallDone } from "../../slice";
import { selectCalls } from "../../selector";
import {
  DEFAULT_PAGE_SIZE,
  pageSizeOptions,
} from "../../../../../utils/pagination";
import { useTableBodyHeight } from "../../../../../utils/useTableBodyHeight";
import { API_ENDPOINT } from "../../../../../utils/constants";
import request, { type RequestError } from "../../../../../utils/request";
import type { CallFilter, DateRange, RecentCall } from "../../types";
import CallDetailModal from "../CallDetailModal";
import SetStatusCell from "../../../../components/SetStatusCell";
import {
  ActionCell,
  CallCard,
  CallCardBottom,
  CallCardTop,
  CallerCell,
  CallerName,
  CallerPhone,
  CallId,
  CallList,
  CallPagination,
  CallMetaText,
  CallPurposeText,
  DateText,
  DateTimeCell,
  DesktopTableWrap,
  EllipsisCellText,
  EntriesFooter,
  FilterButton,
  FilterScroll,
  MobileViewButton,
  RecentCallsCard,
  RecentCallsControls,
  RecentCallsHeader,
  RecentCallsSubtitle,
  RecentCallsTitle,
  RecentCallsTitleWrap,
  SearchInput,
  ShowEntriesSelect,
  ShowEntriesWrap,
  StatusPill,
  StyledTable,
  TimeText,
  ViewButton,
} from "../../style";

const { Text } = Typography;
const { useBreakpoint } = Grid;

const REFRESH_INTERVAL_MS = 2 * 60 * 1000;

interface FilterDef {
  key: CallFilter;
  label: string;
}

const filters: FilterDef[] = [
  { key: "all", label: "All Calls" },
  { key: "successful", label: "Successful Calls" },
  { key: "unsuccessful", label: "Unsuccessful Calls" },
  { key: "followup", label: "Follow-up Calls" },
];

interface RecentCallsProps {
  dateRange: DateRange;
}

export default function RecentCalls({ dateRange }: RecentCallsProps) {
  const dispatch = useDispatch();
  const { data, loading } = useSelector(selectCalls);

  const [filter, setFilter] = useState<CallFilter>("all");
  const [search, setSearch] = useState("");
  const [manualPageSize, setManualPageSize] = useState<number | null>(null);
  const [mobilePage, setMobilePage] = useState(1);
  const [selectedCall, setSelectedCall] = useState<RecentCall | null>(null);
  const [updatingKeys, setUpdatingKeys] = useState<Set<string>>(new Set());
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  async function handleSetDone(row: RecentCall, done: boolean) {
    if (updatingKeys.has(row.key)) return;
    setUpdatingKeys((prev) => new Set(prev).add(row.key));
    const endpoint = done
      ? "mark-registration-call-done"
      : "mark-registration-call-undone";
    try {
      const res = await request<{ is_done: boolean }>(
        `${API_ENDPOINT}${endpoint}?call_log_id=${encodeURIComponent(
          row.callId,
        )}`,
        { method: "PATCH" },
      );
      dispatch(setCallDone({ key: row.key, isDone: res.is_done }));
    } catch (e) {
      // 409 means it was already in the requested state — reflect that.
      if ((e as RequestError)?.status === 409) {
        dispatch(setCallDone({ key: row.key, isDone: done }));
      } else {
        message.error("Could not update status. Please try again.");
      }
    } finally {
      setUpdatingKeys((prev) => {
        const next = new Set(prev);
        next.delete(row.key);
        return next;
      });
    }
  }

  // Manual selection from the "Show entries" control; defaults to 5 everywhere.
  const desktopPageSize = manualPageSize ?? DEFAULT_PAGE_SIZE;
  const mobilePageSize = manualPageSize ?? DEFAULT_PAGE_SIZE;
  const shownPageSize = isMobile ? mobilePageSize : desktopPageSize;

  const [start, end] = dateRange ?? [null, null];
  const startDate = start ? start.format("YYYY-MM-DD") : undefined;
  const endDate = end ? end.format("YYYY-MM-DD") : undefined;

  useEffect(() => {
    const params = { startDate, endDate };
    dispatch(loadCalls(params));
    const intervalId = setInterval(() => {
      dispatch(loadCalls(params));
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [dispatch, startDate, endDate]);

  const filtered = useMemo(() => {
    const calls = data ?? [];
    return calls.filter((c) => {
      if (filter === "successful" && c.status !== "Successful") return false;
      if (filter === "unsuccessful" && c.status !== "Unsuccessful")
        return false;
      if (filter === "followup" && !c.followUp) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (c.callId ?? "").toLowerCase().includes(q) ||
        (c.callerName ?? "").toLowerCase().includes(q) ||
        (c.purpose ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, filter, search]);

  useEffect(() => {
    setMobilePage(1);
  }, [filter, search, mobilePageSize]);

  const pagedMobileCalls = useMemo(() => {
    const start = (mobilePage - 1) * mobilePageSize;
    return filtered.slice(start, start + mobilePageSize);
  }, [filtered, mobilePage, mobilePageSize]);

  const [tableWrapRef, scrollY] = useTableBodyHeight(
    !isMobile,
    desktopPageSize,
    filtered.length,
  );

  const columns: ColumnsType<RecentCall> = [
    {
      title: "Call ID",
      dataIndex: "callId",
      key: "callId",
      width: 100,
      ellipsis: true,
      render: (callId: string) => (
        <Tooltip title={callId}>
          <EllipsisCellText>{callId}</EllipsisCellText>
        </Tooltip>
      ),
    },
    {
      title: "Caller",
      dataIndex: "callerName",
      key: "caller",
      width: 180,
      ellipsis: true,
      render: (_v, row) => (
        <Tooltip title={`${row.callerName} · ${row.callerPhone}`}>
          <CallerCell vertical>
            <CallerName>{row.callerName}</CallerName>
            <CallerPhone>{row.callerPhone}</CallerPhone>
          </CallerCell>
        </Tooltip>
      ),
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      key: "purpose",
      width: 260,
      ellipsis: true,
      render: (purpose: string) => (
        <Tooltip title={purpose}>
          <EllipsisCellText>{purpose}</EllipsisCellText>
        </Tooltip>
      ),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      width: 100,
      align: "center",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      align: "center",
      render: (_v, row) => (
        <StatusPill $success={row.status === "Successful"}>
          {row.status}
        </StatusPill>
      ),
    },
    {
      title: "Date & Time",
      key: "datetime",
      width: 120,
      render: (_v, row) => (
        <DateTimeCell vertical>
          <DateText>{row.date}</DateText>
          <TimeText>{row.time}</TimeText>
        </DateTimeCell>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      render: (_v, row) => (
        <ActionCell align="center" justify="center">
          <ViewButton onClick={() => setSelectedCall(row)}>View</ViewButton>
        </ActionCell>
      ),
    },
    {
      title: "Set status",
      key: "setStatus",
      width: 150,
      align: "center",
      render: (_v, row) => (
        <ActionCell align="center" justify="center">
          <SetStatusCell
            isDone={row.isDone}
            updating={updatingKeys.has(row.key)}
            onMarkDone={() => handleSetDone(row, true)}
            onReset={() => handleSetDone(row, false)}
          />
        </ActionCell>
      ),
    },
  ];

  return (
    <>
      <RecentCallsCard>
        <RecentCallsHeader
          align="flex-start"
          justify="space-between"
          gap={14}
          wrap="wrap"
        >
          <RecentCallsTitleWrap vertical gap={2}>
            <RecentCallsTitle level={5}>Recent Calls</RecentCallsTitle>
            <RecentCallsSubtitle>
              Click 'View' to see the full call details
            </RecentCallsSubtitle>
          </RecentCallsTitleWrap>

          <RecentCallsControls align="center" gap={10} wrap="wrap">
            <SearchInput
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FilterScroll align="center" gap={10}>
              {filters.map((f) => (
                <FilterButton
                  key={f.key}
                  $active={filter === f.key}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </FilterButton>
              ))}
            </FilterScroll>
            {isMobile && (
              <ShowEntriesWrap align="center" gap={8}>
                <Text>Show</Text>
                <ShowEntriesSelect
                  value={shownPageSize}
                  onChange={(v) => setManualPageSize(Number(v))}
                  options={pageSizeOptions(shownPageSize)}
                />
                <Text>entries</Text>
              </ShowEntriesWrap>
            )}
          </RecentCallsControls>
        </RecentCallsHeader>

        {isMobile ? (
          <Spin spinning={loading && !data}>
            <CallList vertical>
              {pagedMobileCalls.map((call) => (
                <CallCard key={call.key}>
                  <CallCardTop align="center" justify="space-between" gap={8}>
                    <CallId>#{call.callId}</CallId>
                    <StatusPill $success={call.status === "Successful"}>
                      {call.status}
                    </StatusPill>
                  </CallCardTop>
                  <CallerName>{call.callerName}</CallerName>
                  <CallerPhone>{call.callerPhone}</CallerPhone>
                  <CallPurposeText>{call.purpose}</CallPurposeText>
                  <CallCardBottom
                    align="center"
                    justify="space-between"
                    gap={8}
                  >
                    <CallMetaText>{call.duration}</CallMetaText>
                    <CallMetaText>
                      {call.date} · {call.time}
                    </CallMetaText>
                  </CallCardBottom>
                  <CallCardBottom align="center" justify="center">
                    <SetStatusCell
                      isDone={call.isDone}
                      updating={updatingKeys.has(call.key)}
                      onMarkDone={() => handleSetDone(call, true)}
                      onReset={() => handleSetDone(call, false)}
                    />
                  </CallCardBottom>
                  <MobileViewButton onClick={() => setSelectedCall(call)}>
                    View
                  </MobileViewButton>
                </CallCard>
              ))}
            </CallList>
            <CallPagination
              current={mobilePage}
              pageSize={mobilePageSize}
              total={filtered.length}
              showSizeChanger={false}
              onChange={(page) => setMobilePage(page)}
              itemRender={(_page, type, originalElement) => {
                if (type === "prev") return <Text>Previous</Text>;
                if (type === "next") return <Text>Next</Text>;
                return originalElement;
              }}
            />
          </Spin>
        ) : (
          <DesktopTableWrap ref={tableWrapRef}>
            <Spin spinning={loading && !data}>
              <StyledTable
                columns={columns as ColumnsType<unknown>}
                dataSource={filtered}
                tableLayout="fixed"
                scroll={{ y: scrollY }}
                pagination={{
                  pageSize: desktopPageSize,
                  showSizeChanger: false,
                  position: ["bottomCenter"],
                  itemRender: (_page, type, originalElement) => {
                    if (type === "prev") return <Text>Previous</Text>;
                    if (type === "next") return <Text>Next</Text>;
                    return originalElement;
                  },
                }}
              />
            </Spin>
            <EntriesFooter align="center" gap={8}>
              <Text>Show</Text>
              <ShowEntriesSelect
                value={shownPageSize}
                onChange={(v) => setManualPageSize(Number(v))}
                options={pageSizeOptions(shownPageSize)}
              />
              <Text>entries</Text>
            </EntriesFooter>
          </DesktopTableWrap>
        )}
      </RecentCallsCard>
      <CallDetailModal
        call={selectedCall}
        open={selectedCall !== null}
        onClose={() => setSelectedCall(null)}
      />
    </>
  );
}
